// Error constants
const CalculatorError = {
  PARENTHESIS_MISMATCH: "Parenthesis mismatch",
  DIVISION_BY_ZERO: "Division by zero", 
  INVALID_EXPRESSION: "Invalid expression",
  UNKNOWN_TOKEN: "Unknown token",
};

/**
 * Calculator class for evaluating mathematical expressions
 * 
 * The class uses a three-step process:
 * 1. Tokenization: Convert the string expression into an array of tokens
 * 2. Parsing: Convert tokens into Reverse Polish Notation (RPN)
 * 3. Computation: Evaluate the RPN to get the final result
 */
class Calculator {
  #expression = "";
  #operators = {
    "+": {
      priority: 1,
      func: (a, b) => a + b,
    },
    "-": {
      priority: 1,
      func: (a, b) => a - b,
    },
    "*": {
      priority: 2,
      func: (a, b) => a * b,
    },
    "/": {
      priority: 2,
      func: (a, b) => {
        if (b === 0) {
          throw new Error(CalculatorError.DIVISION_BY_ZERO);
        }
        return a / b;
      },
    },
    "%": {
      priority: 2,
      func: (a, b) => a % b,
    },
    "^": {
      priority: 3,
      func: (a, b) => Math.pow(a, b),
    },
  };
  #functions = {
    floor: Math.floor,
    ceil: Math.ceil,
  };
  
  // Use Sets for faster lookups
  #functionNamesSet = new Set(["floor", "ceil"]);
  #operatorsSymbolsSet = new Set(["+", "-", "*", "/", "%", "^"]);

  /**
   * Creates a new Calculator instance
   * 
   * @param {string} expression - The mathematical expression to evaluate
   */
  constructor(expression = "") {
    this.#expression = expression;
  }
  
  /**
   * Updates the expression to evaluate without creating a new instance
   * For efficient reuse (especially in performance-critical code)
   * 
   * @param {string} expression - The new mathematical expression to evaluate
   * @returns {Calculator} Returns this instance for method chaining
   */
  setExpression(expression) {
    this.#expression = expression;
    return this;
  }

  /**
   * Tokenizes a mathematical expression into an array of tokens
   * Uses an efficient character-by-character approach without regex splitting
   * 
   * @param {string} expression - The expression to tokenize
   * @returns {string[]} Array of tokens
   * @private
   */
  #tokenizeExpression(expression) {
    const tokens = [];
    let currentNumber = "";
    let currentIdentifier = "";
    
    // Helper to push a number token if we've been building one
    const pushNumber = () => {
      if (currentNumber.length > 0) {
        tokens.push(currentNumber);
        currentNumber = "";
      }
    };
    
    // Helper to push an identifier token if we've been building one
    const pushIdentifier = () => {
      if (currentIdentifier.length > 0) {
        tokens.push(currentIdentifier);
        currentIdentifier = "";
      }
    };
    
    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      
      // Skip spaces
      if (char === " ") {
        pushNumber();
        pushIdentifier();
        continue;
      }
      
      // Handle numbers and decimal points
      if (/[0-9.]/.test(char)) {
        pushIdentifier(); // End any identifier we were building
        currentNumber += char;
        continue;
      }
      
      // Handle operators and parentheses
      if (this.#operatorsSymbolsSet.has(char) || char === "(" || char === ")") {
        pushNumber();
        pushIdentifier();
        
        // Special case for negative numbers (unary minus)
        if (char === "-") {
          const lastToken = tokens[tokens.length - 1];
          if (tokens.length === 0 || lastToken === "(") {
            // Start of expression or after opening paren: treat as unary minus
            tokens.push("0");
          } else if (this.#operatorsSymbolsSet.has(lastToken)) {
            // After another operator: wrap in parens so it binds correctly
            // e.g. "3 * -2" becomes "3 * (0 - 2)" = -6
            tokens.push("(");
            tokens.push("0");
            tokens.push("-");
            // Read ahead to grab the number or sub-expression
            i++; // skip the '-' we already handled
            let num = "";
            while (i < expression.length && /[0-9.]/.test(expression[i])) {
              num += expression[i];
              i++;
            }
            if (num) {
              tokens.push(num);
            }
            tokens.push(")");
            i--; // back up one since the for loop will increment
            continue;
          }
        }

        tokens.push(char);
        continue;
      }
      
      // Must be part of a function name or other identifier
      currentIdentifier += char;
    }
    
    // Push any remaining number or identifier
    pushNumber();
    pushIdentifier();
    
    return tokens;
  }

  /**
   * Parses tokens into Reverse Polish Notation (RPN)
   * Implements the Shunting-yard algorithm for converting infix notation to RPN
   * 
   * @param {string[]} tokens - Array of tokens to parse
   * @returns {Array} Array of tokens in RPN order
   * @private
   */
  #parseTokens(tokens) {
    if (!tokens || tokens.length === 0) {
      return [];
    }
    
    const output = [];
    const operatorStack = [];
    
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      
      // Handle numbers
      if (!isNaN(parseFloat(token))) {
        output.push(parseFloat(token));
        continue;
      }
      
      // Handle functions and opening parenthesis
      if (this.#functionNamesSet.has(token) || token === "(") {
        operatorStack.push(token);
        continue;
      }
      
      // Handle operators
      if (this.#operatorsSymbolsSet.has(token)) {
        while (operatorStack.length > 0) {
          const topOperator = operatorStack[operatorStack.length - 1];
          
          if (!this.#operatorsSymbolsSet.has(topOperator)) {
            break;
          }
          
          const currentPriority = this.#operators[token].priority;
          const topPriority = this.#operators[topOperator].priority;
          
          // Handle operator precedence
          if ((token === "^" && topPriority > currentPriority) || 
              (token !== "^" && topPriority >= currentPriority)) {
            output.push(operatorStack.pop());
          } else {
            break;
          }
        }
        
        operatorStack.push(token);
        continue;
      }
      
      // Handle closing parenthesis
      if (token === ")") {
        let foundMatchingParenthesis = false;
        
        while (operatorStack.length > 0) {
          const topOperator = operatorStack.pop();
          
          if (topOperator === "(") {
            foundMatchingParenthesis = true;
            break;
          }
          
          output.push(topOperator);
        }
        
        if (!foundMatchingParenthesis) {
          throw new Error(CalculatorError.PARENTHESIS_MISMATCH);
        }
        
        // If there's a function at the top of the stack, add it to the output
        if (operatorStack.length > 0 && this.#functionNamesSet.has(operatorStack[operatorStack.length - 1])) {
          output.push(operatorStack.pop());
        }
      }
    }
    
    // Handle remaining operators
    while (operatorStack.length > 0) {
      const operator = operatorStack.pop();
      
      if (operator === "(") {
        throw new Error(CalculatorError.PARENTHESIS_MISMATCH);
      }
      
      output.push(operator);
    }
    
    return output;
  }

  /**
   * Evaluates a Reverse Polish Notation (RPN) expression
   * Uses a stack-based approach for efficient evaluation
   * 
   * @param {Array} rpn - Array of tokens in RPN order
   * @returns {number} The computed result
   * @private
   * @throws {Error} If the expression is invalid or contains errors
   */
  #compute(rpn) {
    if (!rpn || rpn.length === 0) {
      return 0;
    }
    
    const stack = [];
    
    for (const token of rpn) {
      // Handle numbers
      if (typeof token === 'number') {
        stack.push(token);
        continue;
      }
      
      // Handle operators
      if (this.#operatorsSymbolsSet.has(token)) {
        if (stack.length < 2) {
          throw new Error(`${CalculatorError.INVALID_EXPRESSION}: Not enough operands for operator ${token}`);
        }
        
        const b = stack.pop();
        const a = stack.pop();
        
        try {
          const result = this.#operators[token].func(a, b);
          stack.push(result);
        } catch (error) {
          throw error; // Pass through errors like division by zero
        }
        
        continue;
      }
      
      // Handle functions
      if (this.#functionNamesSet.has(token)) {
        if (stack.length < 1) {
          throw new Error(`${CalculatorError.INVALID_EXPRESSION}: Not enough operands for function ${token}`);
        }
        
        const arg = stack.pop();
        const result = this.#functions[token](arg);
        stack.push(result);
        continue;
      }
      
      throw new Error(`${CalculatorError.UNKNOWN_TOKEN}: ${token}`);
    }
    
    if (stack.length !== 1) {
      throw new Error(CalculatorError.INVALID_EXPRESSION);
    }
    
    return stack[0];
  }

  /**
   * Evaluates the current expression and returns the result
   * This is the main public method of the Calculator class
   * 
   * @returns {number} The result of evaluating the expression
   * @throws {Error} If the expression is invalid or contains errors
   * @public
   */
  run() {
    // Handle empty expression
    if (!this.#expression || typeof this.#expression !== 'string' || this.#expression.trim() === '') {
      return 0;
    }
    
    try {
      const tokens = this.#tokenizeExpression(this.#expression);
      if (tokens.length === 0) {
        return 0;
      }
      
      const rpn = this.#parseTokens(tokens);
      return this.#compute(rpn);
    } catch (error) {
      // Re-throw calculation errors
      throw error;
    }
  }
}

// Create a singleton instance for reuse
const calculator = new Calculator();

export { Calculator, CalculatorError, calculator };